/**
 * Jurisdiction Requestor Defense-in-Depth Guard.
 *
 * Per Session 24 (Feb 22, 2026), accounts with the
 * `jurisdiction_requestor` role MUST NEVER access the operational
 * order dashboard, processor orders, order details, or related
 * routes. They land exclusively on `/admin/my-jurisdiction-dashboard`.
 *
 * The canonical truth lives in `user.role` (snake_case enum). However,
 * legacy / cached / partially-shaped user records may carry the role
 * indicator in any of these places:
 *
 *   • user.role          → "jurisdiction_requestor"  (canonical)
 *   • user.roles[]       → ["jurisdiction_requestor"] OR ["Jurisdiction Requestor"]
 *   • user.role_ids[]    → ["<role UUID>"]            (admin list shape)
 *
 * This helper returns `true` if ANY of those signals indicate the
 * jurisdiction_requestor persona, so the redirect guards still fire
 * even when the front-end is reading a stale or partially-shaped user
 * record (e.g. from an old localStorage snapshot, the admin users list
 * payload, or a pre-Session-24 token).
 *
 * The check is intentionally tolerant of:
 *   - case differences ("Jurisdiction Requestor" vs "jurisdiction_requestor")
 *   - whitespace and hyphens vs underscores
 *   - missing fields (returns false safely)
 */

const REQUESTOR_TOKENS = new Set([
  'jurisdiction_requestor',
  'jurisdictionrequestor',
]);

const normalize = (val) =>
  String(val || '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-]+/g, '_')
    .replace(/_+/g, '_');

const matchesRequestor = (val) => {
  if (val === undefined || val === null) return false;
  const n = normalize(val);
  if (!n) return false;
  // Direct match
  if (REQUESTOR_TOKENS.has(n)) return true;
  // Strip the underscore form for "jurisdictionrequestor"
  if (n.replace(/_/g, '') === 'jurisdictionrequestor') return true;
  return false;
};

/**
 * Returns true if the user object indicates the jurisdiction_requestor
 * persona — checking `role`, `roles[]`, and `role_ids[]` (when seeded
 * with a known requestor UUID via `setKnownRequestorRoleId`).
 *
 * @param {object} user - User record (any shape)
 * @returns {boolean}
 */
export const isJurisdictionRequestor = (user) => {
  if (!user || typeof user !== 'object') return false;

  // Primary: canonical `role` field
  if (matchesRequestor(user.role)) return true;

  // Fallback: legacy `roles[]` array (may contain enums OR display labels)
  if (Array.isArray(user.roles)) {
    for (const r of user.roles) {
      if (matchesRequestor(r)) return true;
    }
  }

  // Fallback: `role_ids[]` array (admin list shape) — only useful if
  // we've previously cached the requestor role's UUID.
  const cachedUuid = sessionStorage.getItem('jurisdictionRequestorRoleUuid');
  if (cachedUuid && Array.isArray(user.role_ids)) {
    if (user.role_ids.includes(cachedUuid)) return true;
  }

  return false;
};

/**
 * Optional: cache the jurisdiction_requestor role UUID so the
 * `role_ids[]` defense fires too. Called once after the admin roles
 * list is fetched.
 *
 * @param {string} uuid
 */
export const setKnownRequestorRoleId = (uuid) => {
  if (uuid && typeof uuid === 'string') {
    sessionStorage.setItem('jurisdictionRequestorRoleUuid', uuid);
  }
};

/**
 * Read the cached requestor role UUID if available.
 *
 * @returns {string|null}
 */
export const getKnownRequestorRoleId = () =>
  sessionStorage.getItem('jurisdictionRequestorRoleUuid') || null;
